import { call, put, select, StrictEffect, takeEvery } from "redux-saga/effects";

import { DataType } from "@/jenesys-types/data/enums";
import { extractError } from "@/jenesys-types/errors";
import { EmailForwardingSender } from "@/jenesys-types/jenesys-entities/config";

import { performDataAction } from "@/services/api";
import { selectActiveCompanyId } from "@/store/features/companies/companies";

import {
  addSender,
  addSenderFailure,
  addSenderSuccess,
  loadSenders,
  loadSendersFailure,
  loadSendersSuccess,
  updateSender,
  updateSenderFailure,
  updateSenderSuccess,
  uploadCSV,
  uploadCSVFailure,
  uploadCSVSuccess
} from "./emailForwardingSenders";

export function* loadSendersSaga(
  action: ReturnType<typeof loadSenders>
): Generator<StrictEffect, void, any> {
  const { companyId } = action.payload;
  const activeCompanyId = yield select(selectActiveCompanyId);
  try {
    const response: EmailForwardingSender[] = yield call(
      performDataAction,
      DataType.EmailForwardingSender,
      "list",
      {
        companyId: companyId ?? activeCompanyId
      }
    );

    yield put(loadSendersSuccess(response));
  } catch (error) {
    yield put(loadSendersFailure(extractError(error)));
  }
}

export function* addSenderSaga(
  action: ReturnType<typeof addSender>
): Generator<StrictEffect, void, any> {
  const { companyId, data, onSuccess } = action.payload;
  const activeCompanyId = yield select(selectActiveCompanyId);
  try {
    const response = yield call(
      performDataAction,
      DataType.EmailForwardingSender,
      "create",
      {
        companyId: companyId ?? activeCompanyId
      },
      undefined,
      data
    );
    yield put(addSenderSuccess(response));
    yield put(loadSenders(companyId ?? activeCompanyId));
    if (onSuccess) onSuccess();
  } catch (error) {
    yield put(addSenderFailure(extractError(error)));
  }
}

export function* updateSenderSaga(
  action: ReturnType<typeof updateSender>
): Generator<StrictEffect, void, any> {
  const { companyId, id, data, onSuccess } = action.payload;
  const activeCompanyId = yield select(selectActiveCompanyId);
  try {
    const response = yield call(
      performDataAction,
      DataType.EmailForwardingSender,
      "update",
      {
        companyId: companyId ?? activeCompanyId,
        id
      },
      undefined,
      data
    );
    yield put(updateSenderSuccess(response));
    yield put(loadSenders(companyId ?? activeCompanyId));
    if (onSuccess) onSuccess();
  } catch (error) {
    yield put(updateSenderFailure(extractError(error)));
  }
}

export function* uploadCSVSaga(
  action: ReturnType<typeof uploadCSV>
): Generator<StrictEffect, void, any> {
  const { companyId, file, onSuccess } = action.payload;
  const activeCompanyId = yield select(selectActiveCompanyId);
  const formData = new FormData();

  formData.append("csv_file", file);
  try {
    const response = yield call(
      performDataAction,
      DataType.EmailForwardingSender,
      "upload",
      {
        companyId: companyId ?? activeCompanyId
      },
      undefined,
      formData
    );
    yield put(uploadCSVSuccess(response));
    yield put(loadSenders(companyId ?? activeCompanyId));
    if (onSuccess) onSuccess();
  } catch (error) {
    yield put(uploadCSVFailure(extractError(error)));
  }
}

export default function* emailForwardingSendersSaga() {
  yield takeEvery(loadSenders.type, loadSendersSaga);
  yield takeEvery(addSender.type, addSenderSaga);
  yield takeEvery(updateSender.type, updateSenderSaga);
  yield takeEvery(uploadCSV.type, uploadCSVSaga);
}
